import { AuditEventList, AuditFilters } from "@/components/audit/AuditEvents";
import { PageHeader } from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { BOO_AUDIT_CATEGORIES } from "@/lib/audit-copy";
import { type AuditSearch, auditQueryOf, currentMinute, hasAuditFilters } from "@/lib/audit-search";
import { useSuperAdminView } from "@/lib/use-superadmin-view";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

// One Boo's audit log. Its owners and admins see everything, including what the
// Boo itself did (apps and connectors it used, approvals, workflows); members see
// the changes made to it, without secrets, IP addresses or browsers.

const PAGE_SIZE = 50;

export function ActivityTab() {
  const { orgId, containerId } = useParams({ strict: false }) as { orgId: string; containerId: string };
  const urlSearch = useSearch({ strict: false }) as AuditSearch;
  const navigate = useNavigate();
  const staffView = useSuperAdminView();
  const now = currentMinute();

  const setSearch = (patch: Partial<AuditSearch>) =>
    navigate({
      to: "/orgs/$orgId/containers/$containerId/activity",
      params: { orgId, containerId },
      search: (prev: AuditSearch) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
        return next;
      },
      replace: true,
    } as never);

  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const org = useQuery({ queryKey: ["org-full", orgId], queryFn: () => api.org.details() });
  const perms = boos.data?.data.find((b) => b.id === containerId)?._permissions;
  const manages = perms?.canManageMembers === true;
  const categories = BOO_AUDIT_CATEGORIES.filter(
    (c) => (c !== "boo_activity" || manages) && (c !== "secrets" || perms?.canReadSecrets === true),
  );
  // The Boo filter is the tab itself, and a shared link's category this viewer can't see is dropped.
  const search: AuditSearch = {
    ...urlSearch,
    boo: undefined,
    category: urlSearch.category && categories.includes(urlSearch.category) ? urlSearch.category : undefined,
  };

  const query = auditQueryOf(search, now);
  const events = useInfiniteQuery({
    queryKey: ["boo-audit", containerId, query],
    queryFn: ({ pageParam }) => api.containers.audit.list(containerId, { ...query, before: pageParam ?? undefined, limit: PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto p-6">
      <PageHeader
        title="Activity"
        subtitle={
          manages
            ? "What this Boo did with apps and connectors, what people approved, and every change made to it, Boo Lab support visits included."
            : "Changes made to this Boo and Boo Lab support visits. Its owners and admins also see what it did with apps and connectors."
        }
      />
      <section className="flex flex-col gap-4" aria-label="Activity">
        <AuditFilters
          search={search}
          onChange={setSearch}
          categories={categories}
          members={org.data?.data.members ?? []}
          end={
            staffView && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={!!search.internal} onCheckedChange={(on) => setSearch({ internal: on ? true : undefined })} aria-label="Include Boo Lab-only events" />
                Include Boo Lab-only events
              </label>
            )
          }
        />
        <AuditEventList
          events={events}
          filtered={hasAuditFilters(search)}
          showBoo={false}
          emptyText="Nothing recorded yet. What this Boo does and changes to it show up here as they happen."
        />
      </section>
    </div>
  );
}
